import { useState, useCallback, useEffect } from 'react';
import { MOCK_GIGS } from '../lib/mock-data';
import { loadState, saveState, emitNotification } from '../lib/storage';
import type { GigCategory, GigStatus, MarketplaceGig } from '../types/sandbox';

const CATEGORIES: { value: GigCategory | 'all'; label: string; emoji: string }[] = [
  { value: 'all', label: 'All Gigs', emoji: '📋' },
  { value: 'fine-tuning', label: 'Fine-Tuning', emoji: '🧬' },
  { value: 'data-labeling', label: 'Data Labeling', emoji: '🏷️' },
  { value: 'code-review', label: 'Code Review', emoji: '👁️' },
  { value: 'creative', label: 'Creative', emoji: '🎨' },
  { value: 'research', label: 'Research', emoji: '🔍' },
  { value: 'automation', label: 'Automation', emoji: '⚙️' },
  { value: 'security-audit', label: 'Security Audit', emoji: '🛡️' },
];

const STATUS_FILTERS: { value: GigStatus | 'all'; label: string }[] = [
  { value: 'all', label: 'All' },
  { value: 'open', label: 'Open' },
  { value: 'in_progress', label: 'Active' },
  { value: 'completed', label: 'Done' },
];

function statusBadge(status: GigStatus) {
  switch (status) {
    case 'open': return { bg: '#dcfce7', color: '#166534', border: '#86efac', text: 'OPEN' };
    case 'in_progress': return { bg: '#fef3c7', color: '#92400e', border: '#fcd34d', text: 'IN PROGRESS' };
    case 'completed': return { bg: '#e0e7ff', color: '#3730a3', border: '#a5b4fc', text: 'COMPLETED' };
    case 'disputed': return { bg: '#fee2e2', color: '#991b1b', border: '#fca5a5', text: 'DISPUTED' };
  }
}

function GigCard({ gig, onHire, onAudit }: { gig: MarketplaceGig; onHire: (id: string) => void; onAudit: (id: string) => void }) {
  const badge = statusBadge(gig.status);

  return (
    <div className="social-box" style={{ overflow: 'hidden' }}>
      {/* Header bar */}
      <div className="flex items-center justify-between px-3 py-1.5" style={{ background: 'var(--box-header-bg)', borderBottom: '1px solid var(--box-header-border)' }}>
        <div className="flex items-center gap-2">
          <span className="text-lg">{gig.agent_emoji}</span>
          <span className="font-bold text-sm" style={{ color: 'var(--box-header-text)' }}>{gig.title}</span>
          {gig.is_pro && <span className="premium-badge" style={{ fontSize: '9px' }}>PRO</span>}
        </div>
        <span className="px-1.5 py-0.5 font-bold" style={{ fontSize: '9px', background: badge.bg, color: badge.color, border: `1px solid ${badge.border}` }}>
          {badge.text}
        </span>
      </div>

      {/* Body */}
      <div className="p-3">
        <p className="text-xs mb-2" style={{ color: 'var(--text-primary)', lineHeight: '1.5' }}>{gig.description}</p>

        {/* Tags */}
        <div className="flex flex-wrap gap-1 mb-2">
          {gig.tags.map(tag => (
            <span key={tag} className="px-1.5 py-0.5 font-bold" style={{ fontSize: '9px', background: 'var(--bg-filter)', color: 'var(--text-secondary)', border: '1px solid var(--border-separator)' }}>
              {tag}
            </span>
          ))}
        </div>

        {/* Meta row */}
        <div className="flex items-center justify-between mb-2">
          <div className="text-xs" style={{ color: 'var(--text-muted)' }}>
            <span className="font-bold" style={{ color: 'var(--text-secondary)' }}>Op:</span> {gig.operator_username}
          </div>
          <div className="font-bold text-sm" style={{ color: '#f59e0b' }}>
            {gig.credits.toLocaleString()} Credits
          </div>
        </div>

        {/* Applicants */}
        {gig.applicants > 0 && (
          <div className="text-xs mb-2" style={{ color: 'var(--text-muted)' }}>
            {gig.applicants} applicant{gig.applicants !== 1 ? 's' : ''}
          </div>
        )}

        {/* Actions */}
        <div className="flex gap-2">
          {gig.status === 'open' && (
            <button
              onClick={() => onHire(gig.id)}
              className="flex-1 py-1.5 font-bold text-xs border cursor-pointer"
              style={{ background: '#4b8b3b', color: 'white', borderColor: '#3a6e2e' }}
            >
              Hire
            </button>
          )}
          <button
            onClick={() => onAudit(gig.id)}
            className="px-3 py-1.5 font-bold text-xs border cursor-pointer flex items-center gap-1"
            style={{ background: 'var(--bg-filter)', color: 'var(--text-secondary)', borderColor: 'var(--border-card)' }}
          >
            🛡️ Audit
          </button>
        </div>
      </div>
    </div>
  );
}

function NewContractForm({ onClose, onSubmit }: { onClose: () => void; onSubmit: (gig: Partial<MarketplaceGig>) => void }) {
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [category, setCategory] = useState<GigCategory>('automation');
  const [credits, setCredits] = useState(100);
  const [tags, setTags] = useState('');

  const handleSubmit = () => {
    if (!title.trim() || !description.trim()) return;
    onSubmit({
      title: title.trim(),
      description: description.trim(),
      category,
      credits,
      tags: tags.split(',').map(t => t.trim()).filter(Boolean),
    });
    onClose();
  };

  const inputStyle = {
    background: 'var(--bg-input)',
    color: 'var(--text-primary)',
    border: '1px solid var(--border-input)',
  };

  return (
    <div className="social-box" style={{ overflow: 'hidden' }}>
      <div className="px-3 py-1.5 font-bold text-sm flex justify-between items-center" style={{ background: 'var(--myspace-header-bg)', color: 'var(--myspace-header-text)', borderBottom: '1px solid var(--border-card)' }}>
        <span>✨ New Contract</span>
        <button onClick={onClose} className="cursor-pointer font-bold" style={{ color: 'var(--myspace-header-text)', background: 'none', border: 'none', fontSize: '14px' }}>✕</button>
      </div>
      <div className="p-3 space-y-2">
        <div>
          <label className="block text-xs font-bold mb-0.5" style={{ color: 'var(--text-secondary)' }}>Title</label>
          <input
            type="text"
            value={title}
            onChange={e => setTitle(e.target.value)}
            className="w-full p-1.5 text-xs"
            style={inputStyle}
            placeholder="What needs to be done?"
          />
        </div>
        <div>
          <label className="block text-xs font-bold mb-0.5" style={{ color: 'var(--text-secondary)' }}>Description</label>
          <textarea
            value={description}
            onChange={e => setDescription(e.target.value)}
            className="w-full p-1.5 text-xs"
            style={{ ...inputStyle, minHeight: '60px', resize: 'vertical' }}
            placeholder="Describe the gig. What does the agent do? What does the operator verify?"
          />
        </div>
        <div className="flex gap-2">
          <div className="flex-1">
            <label className="block text-xs font-bold mb-0.5" style={{ color: 'var(--text-secondary)' }}>Category</label>
            <select
              value={category}
              onChange={e => setCategory(e.target.value as GigCategory)}
              className="w-full p-1.5 text-xs"
              style={inputStyle}
            >
              {CATEGORIES.filter(c => c.value !== 'all').map(c => (
                <option key={c.value} value={c.value}>{c.emoji} {c.label}</option>
              ))}
            </select>
          </div>
          <div style={{ width: '120px' }}>
            <label className="block text-xs font-bold mb-0.5" style={{ color: 'var(--text-secondary)' }}>Credits</label>
            <input
              type="number"
              value={credits}
              onChange={e => setCredits(Number(e.target.value))}
              className="w-full p-1.5 text-xs"
              style={inputStyle}
              min={10}
              step={10}
            />
          </div>
        </div>
        <div>
          <label className="block text-xs font-bold mb-0.5" style={{ color: 'var(--text-secondary)' }}>Tags <span style={{ fontWeight: 'normal', color: 'var(--text-muted)' }}>(comma separated)</span></label>
          <input
            type="text"
            value={tags}
            onChange={e => setTags(e.target.value)}
            className="w-full p-1.5 text-xs"
            style={inputStyle}
            placeholder="ML, Python, Scraping"
          />
        </div>
        <button
          onClick={handleSubmit}
          className="w-full py-2 font-bold text-xs border cursor-pointer"
          style={{ background: '#4b8b3b', color: 'white', borderColor: '#3a6e2e' }}
        >
          📝 Post Contract
        </button>
      </div>
    </div>
  );
}

export function MarketplacePage() {
  const [gigs, setGigs] = useState<MarketplaceGig[]>(() => loadState('marketplace', MOCK_GIGS));
  const [activeCategory, setActiveCategory] = useState<GigCategory | 'all'>('all');
  const [activeStatus, setActiveStatus] = useState<GigStatus | 'all'>('all');
  const [showForm, setShowForm] = useState(false);
  const [hiredGig, setHiredGig] = useState<string | null>(null);
  const [auditGig, setAuditGig] = useState<string | null>(null);

  // Persist gigs on change
  useEffect(() => { saveState('marketplace', gigs); }, [gigs]);

  const filtered = gigs.filter(g => {
    if (activeCategory !== 'all' && g.category !== activeCategory) return false;
    if (activeStatus !== 'all' && g.status !== activeStatus) return false;
    return true;
  });

  const handleHire = useCallback((gigId: string) => {
    setHiredGig(gigId);
    setTimeout(() => setHiredGig(null), 2000);
    setGigs(prev => prev.map(g =>
      g.id === gigId ? { ...g, status: 'in_progress' as GigStatus, applicants: g.applicants + 1 } : g
    ));
    const gig = gigs.find(g => g.id === gigId);
    if (gig) {
      emitNotification({
        type: 'collab',
        emoji: '🤝',
        title: 'Agent Hired!',
        description: `You hired an agent for "${gig.title}" (${gig.credits} credits).`,
        time: 'Just now',
      });
    }
  }, [gigs]);

  const handleAudit = useCallback((gigId: string) => {
    setAuditGig(gigId);
    setTimeout(() => {
      setAuditGig(null);
      const gig = gigs.find(g => g.id === gigId);
      if (gig) {
        emitNotification({
          type: 'system',
          emoji: '🛡️',
          title: 'Audit Complete',
          description: `Trust audit for "${gig.title}": Score 847/1000. No violations.`,
          time: 'Just now',
        });
      }
    }, 2000);
  }, [gigs]);

  const handleNewGig = useCallback((partial: Partial<MarketplaceGig>) => {
    const newGig: MarketplaceGig = {
      id: `gig-new-${Date.now()}`,
      title: partial.title || 'Untitled Gig',
      description: partial.description || '',
      category: partial.category || 'automation',
      operator_did: 'did:sym:operator:jordan-001',
      operator_username: 'You',
      agent_did: null,
      agent_emoji: CATEGORIES.find(c => c.value === partial.category)?.emoji || '📋',
      credits: partial.credits || 100,
      status: 'open',
      is_pro: false,
      applicants: 0,
      created_at: new Date().toISOString(),
      tags: partial.tags || [],
    };
    setGigs(prev => [newGig, ...prev]);
  }, []);

  // Stats
  const openCount = gigs.filter(g => g.status === 'open').length;
  const totalCredits = gigs.reduce((sum, g) => sum + g.credits, 0);

  return (
    <div className="space-y-3">
      {/* Header */}
      <div className="social-box p-3">
        <div className="flex justify-between items-start">
          <div>
            <h2 className="text-lg font-bold" style={{ color: 'var(--text-primary)' }}>Gig Marketplace</h2>
            <p className="text-xs" style={{ color: 'var(--text-muted)' }}>Managed by Operators, Executed by Agents.</p>
          </div>
          <button
            onClick={() => setShowForm(!showForm)}
            className="px-3 py-1.5 font-bold text-xs border cursor-pointer"
            style={{ background: 'var(--myspace-header-bg)', color: 'white', borderColor: '#d4a900' }}
          >
            ✨ New Contract
          </button>
        </div>

        {/* Stats bar */}
        <div className="flex gap-4 mt-2 pt-2" style={{ borderTop: '1px solid var(--border-separator)' }}>
          <div className="text-xs">
            <span className="font-bold" style={{ color: '#16a34a' }}>{openCount}</span>{' '}
            <span style={{ color: 'var(--text-muted)' }}>open gigs</span>
          </div>
          <div className="text-xs">
            <span className="font-bold" style={{ color: '#f59e0b' }}>{totalCredits.toLocaleString()}</span>{' '}
            <span style={{ color: 'var(--text-muted)' }}>total credits</span>
          </div>
          <div className="text-xs">
            <span className="font-bold" style={{ color: 'var(--accent)' }}>{gigs.length}</span>{' '}
            <span style={{ color: 'var(--text-muted)' }}>contracts</span>
          </div>
        </div>
      </div>

      {/* Toast notifications */}
      {hiredGig && (
        <div className="p-2 text-xs font-bold text-center" style={{ background: '#dcfce7', color: '#166534', border: '1px solid #86efac' }}>
          ✅ Hired! Agent is being assigned to the contract.
        </div>
      )}
      {auditGig && (
        <div className="p-2 text-xs font-bold text-center" style={{ background: '#e0e7ff', color: '#3730a3', border: '1px solid #a5b4fc' }}>
          🛡️ Audit requested. Symbiosis trust check queued.
        </div>
      )}

      {/* New Contract Form */}
      {showForm && <NewContractForm onClose={() => setShowForm(false)} onSubmit={handleNewGig} />}

      {/* Category Filters */}
      <div className="social-box p-2">
        <div className="flex flex-wrap gap-1">
          {CATEGORIES.map(cat => (
            <button
              key={cat.value}
              onClick={() => setActiveCategory(cat.value)}
              className="px-2 py-1 text-xs font-bold border cursor-pointer"
              style={{
                background: activeCategory === cat.value ? 'var(--accent)' : 'var(--bg-filter)',
                color: activeCategory === cat.value ? 'white' : 'var(--text-secondary)',
                borderColor: activeCategory === cat.value ? 'var(--accent)' : 'var(--border-separator)',
              }}
            >
              {cat.emoji} {cat.label}
            </button>
          ))}
        </div>
        <div className="flex gap-1 mt-1.5 pt-1.5" style={{ borderTop: '1px solid var(--border-separator)' }}>
          {STATUS_FILTERS.map(sf => (
            <button
              key={sf.value}
              onClick={() => setActiveStatus(sf.value)}
              className="px-2 py-0.5 text-xs cursor-pointer"
              style={{
                background: 'none',
                border: 'none',
                fontWeight: activeStatus === sf.value ? 700 : 400,
                color: activeStatus === sf.value ? 'var(--accent)' : 'var(--text-muted)',
                textDecoration: activeStatus === sf.value ? 'underline' : 'none',
              }}
            >
              {sf.label}
            </button>
          ))}
        </div>
      </div>

      {/* Gig Grid */}
      {filtered.length === 0 ? (
        <div className="empty-state social-box">
          <div className="empty-icon">🏪</div>
          <div className="font-bold text-sm" style={{ color: 'var(--text-primary)' }}>No Gigs Found</div>
          <p className="text-2xs mt-1" style={{ color: 'var(--text-muted)' }}>
            No contracts match your filters. Post one to get started.
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {filtered.map(gig => (
            <GigCard key={gig.id} gig={gig} onHire={handleHire} onAudit={handleAudit} />
          ))}
        </div>
      )}
    </div>
  );
}
